import type { Selector } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";

import type { IShopRootState } from "@/shop/reducers/types";
import type { IMultiSearchResultItem } from "@/shop/types/multiSearch";
import { MultiSearchSource } from "@/shop/types/multiSearch";

import { MULTISEARCH_MINIMUM_LENGTH_SEARCHTERM, MULTISEARCH_TEXT_FILTER_REGEX } from "./constants";
import type { IMultiSearchQueryState, IMultiSearchResultState, IMultiSearchState } from "./types";

// state slices
export const multiSearchStateSelector: Selector<IShopRootState, IMultiSearchState> = (state) => state.multiSearch;
export const multiSearchQueryStateSelector: Selector<IShopRootState, IMultiSearchQueryState> = (state) => state.multiSearch.query;
export const multiSearchResultsStateSelector: Selector<IShopRootState, { [id in MultiSearchSource]: IMultiSearchResultState }> = (state) =>
  state.multiSearch.results;

// TODO: fix this later
export const getMultiSearchResults: Selector<IShopRootState, { [id in MultiSearchSource]: IMultiSearchResultState }> = createSelector(
  multiSearchResultsStateSelector,
  (state) => state
);

export const getMultiSearchResultsStates: Selector<IShopRootState, IMultiSearchResultState[]> = createSelector(multiSearchResultsStateSelector, (state) => [
  state[MultiSearchSource.Parts],
  state[MultiSearchSource.OriginalSupply],
  state[MultiSearchSource.Orders],
  state[MultiSearchSource.Quotes],
  state[MultiSearchSource.IBase],
  state[MultiSearchSource.FrsProduct],
  state[MultiSearchSource.CeSparepart],
  state[MultiSearchSource.FavoritList],
  state[MultiSearchSource.Service],
  state[MultiSearchSource.ToscotecSp]
]);

export const getUnifiedResultList: Selector<IShopRootState, IMultiSearchResultItem[]> = createSelector(getMultiSearchResultsStates, (groups) =>
  groups
    .filter((g) => g.data != null)
    .map((g) => g.data.results)
    .reduce((memo, val) => memo.concat(val), [])
);

export const getUnifiedIsFetching: Selector<IShopRootState, boolean> = createSelector(getMultiSearchResultsStates, (groups) =>
  groups.reduce((memo, g) => memo || g.isFetching, false)
);

export const getUnifiedHasData: Selector<IShopRootState, boolean> = createSelector(getMultiSearchResultsStates, (groups) =>
  groups.reduce((memo, g) => memo && g.data != null, true)
);

export const getMultiSearchIsDone: Selector<IShopRootState, boolean> = createSelector(
  getUnifiedIsFetching,
  getUnifiedHasData,
  (isFetching, hasData) => !isFetching && hasData
);

export const getUnifiedNumbers: Selector<IShopRootState, string[]> = createSelector(getUnifiedResultList, (results) => {
  const validResults = results.filter(Boolean); // there seem to be cases with null valued array items
  return [
    ...validResults.map((res) => res.number),
    ...validResults.map((res) => res.customerMaterialNumber!),
    ...validResults.reduce((memo, res) => memo.concat(res.oldNumbers), [] as string[])
  ].filter(Boolean);
});

export const getMultiSearchText: Selector<IShopRootState, string> = createSelector(multiSearchQueryStateSelector, (state) =>
  state.textValue.replace(/\//g, "")
);

export const getMultiSearchTokenizedQueryTerms: Selector<IShopRootState, string[]> = createSelector(
  multiSearchQueryStateSelector,
  (state) => state.tokenizedValue
);

export const getMultiSearchTokenizedQuery: Selector<IShopRootState, { term: string; found: boolean | null }[]> = createSelector(
  getMultiSearchTokenizedQueryTerms,
  getUnifiedNumbers,
  getMultiSearchIsDone,
  (terms, matches, isDone) =>
    terms
      .filter((item) => item.length >= MULTISEARCH_MINIMUM_LENGTH_SEARCHTERM)
      .map((term) => ({
        term: term.replace(MULTISEARCH_TEXT_FILTER_REGEX, ""),
        found: matches.map(normalizeMaterialNumber).indexOf(normalizeMaterialNumber(term)) >= 0 ? true : isDone ? false : null
      }))
);

function normalizeMaterialNumber(s: string): string {
  return s.replace(/[.]/g, "").toLowerCase();
}
